import axios from 'axios';
import {API_BASE_URL, AuthUserEnterpriseSummary} from './authApi';

export type EnterpriseInviteRole = 'admin' | 'coach' | 'recruiter' | 'staff';
export type EnterpriseMemberRole = AuthUserEnterpriseSummary['role'];
export type EnterpriseMemberStatus = 'active' | 'inactive';
export type EnterpriseInviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface EnterpriseAccountSummary {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  seatLimit: number;
  activeSeatCount: number;
  pendingInviteCount: number;
}

export interface EnterpriseMembershipSummary extends AuthUserEnterpriseSummary {
  membershipId: string;
}

export interface EnterpriseMeResponse {
  success: boolean;
  organization: EnterpriseAccountSummary;
  membership: EnterpriseMembershipSummary;
}

export interface EnterpriseMemberRecord {
  id: string;
  userId: string;
  email: string;
  name: string;
  avatar?: string;
  role: EnterpriseMemberRole;
  status: EnterpriseMemberStatus;
  joinedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseInviteRecord {
  id: string;
  email: string;
  role: EnterpriseInviteRole;
  status: EnterpriseInviteStatus;
  organizationId: string;
  organizationName: string;
  expiresAt: string;
  lastSentAt: string;
  createdAt: string;
  acceptedAt?: string;
  inviteUrl?: string;
}

export interface EnterpriseInviteClaim {
  email: string;
  role: EnterpriseInviteRole;
  organizationId: string;
  organizationName: string;
  expiresAt: string;
  hasExistingUser: boolean;
}

type ApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const enterpriseClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const authorizedHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

export const enterpriseApi = {
  async getMe(token: string): Promise<EnterpriseMeResponse> {
    try {
      const response = await enterpriseClient.get<EnterpriseMeResponse>('/enterprise/me', {
        headers: authorizedHeaders(token),
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load Enterprise account'));
    }
  },

  async getMembers(token: string): Promise<EnterpriseMemberRecord[]> {
    try {
      const response = await enterpriseClient.get<{success: boolean; members: EnterpriseMemberRecord[]}>('/enterprise/members', {
        headers: authorizedHeaders(token),
      });
      return response.data.members;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load Enterprise members'));
    }
  },

  async getInvites(token: string): Promise<EnterpriseInviteRecord[]> {
    try {
      const response = await enterpriseClient.get<{success: boolean; invites: EnterpriseInviteRecord[]}>('/enterprise/invites', {
        headers: authorizedHeaders(token),
      });
      return response.data.invites;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load Enterprise invites'));
    }
  },

  async createInvite(token: string, payload: {email: string; role: EnterpriseInviteRole}): Promise<EnterpriseInviteRecord> {
    try {
      const response = await enterpriseClient.post<{success: boolean; invite: EnterpriseInviteRecord}>(
        '/enterprise/invites',
        payload,
        {headers: authorizedHeaders(token)}
      );
      return response.data.invite;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to create Enterprise invite'));
    }
  },

  async claimInvite(inviteToken: string): Promise<EnterpriseInviteClaim> {
    try {
      const response = await enterpriseClient.post<{success: boolean; invite: EnterpriseInviteClaim}>(
        `/enterprise/invites/${inviteToken}/claim`
      );
      return response.data.invite;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to validate Enterprise invite'));
    }
  },

  async acceptInvite(token: string, inviteToken: string): Promise<AuthUserEnterpriseSummary | null> {
    try {
      const response = await enterpriseClient.post<{success: boolean; enterprise: AuthUserEnterpriseSummary | null}>(
        `/enterprise/invites/${inviteToken}/accept`,
        undefined,
        {headers: authorizedHeaders(token)}
      );
      return response.data.enterprise;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to accept Enterprise invite'));
    }
  },

  async updateMember(
    token: string,
    memberId: string,
    payload: {role?: EnterpriseInviteRole; status?: EnterpriseMemberStatus}
  ): Promise<EnterpriseMemberRecord> {
    try {
      const response = await enterpriseClient.patch<{success: boolean; member: EnterpriseMemberRecord}>(
        `/enterprise/members/${memberId}`,
        payload,
        {headers: authorizedHeaders(token)}
      );
      return response.data.member;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to update Enterprise member'));
    }
  },

  async resendInvite(token: string, inviteId: string): Promise<EnterpriseInviteRecord> {
    try {
      const response = await enterpriseClient.post<{success: boolean; invite: EnterpriseInviteRecord}>(
        `/enterprise/invites/${inviteId}/resend`,
        undefined,
        {headers: authorizedHeaders(token)}
      );
      return response.data.invite;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to resend Enterprise invite'));
    }
  },

  async revokeInvite(token: string, inviteId: string): Promise<EnterpriseInviteRecord> {
    try {
      const response = await enterpriseClient.post<{success: boolean; invite: EnterpriseInviteRecord}>(
        `/enterprise/invites/${inviteId}/revoke`,
        undefined,
        {headers: authorizedHeaders(token)}
      );
      return response.data.invite;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to revoke Enterprise invite'));
    }
  },
};
